import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IdCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { adminApi, errorMessage } from "../../lib/api";
import { DocPreview } from "./DocPreview";
import { KycBadge } from "./KycBadge";
import { PhotoThumb } from "./PhotoThumb";
import { spacedAadhaar } from "./adminConfig";

const IdCardButton = ({ lead }) => {
  const open = () => {
    const win = window.open("", "_blank");
    adminApi.get(`/admin/id-card/${lead.id}`)
      .then((r) => { if (win) win.location.href = `/id/${r.data.token}`; else window.location.href = `/id/${r.data.token}`; })
      .catch((e) => { win?.close(); toast.error(errorMessage(e)); });
  };
  return (
    <button onClick={open} data-testid="kyc-id-card-button" className="border border-sky-400/50 px-5 py-2.5 text-sm font-semibold text-sky-300 hover:bg-sky-500/10">
      <IdCard className="mr-2 inline h-4 w-4" />ID Card
    </button>
  );
};

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-2 text-sm">
    <span className="text-slate-400">{label}</span>
    <span data-testid={testId} className="text-right font-mono text-slate-100">{value || "—"}</span>
  </div>
);

const AadhaarRow = ({ kind, lead }) => {
  const [full, setFull] = useState(null);
  useEffect(() => { setFull(lead.aadhaar_full ? spacedAadhaar(lead.aadhaar_full) : null); }, [lead.id, lead.aadhaar_full]);
  const show = () => adminApi.post(`/admin/leads/${kind}/${lead.id}/aadhaar`)
    .then((r) => setFull(r.data.aadhaar_number.replace(/(\d{4})(?=\d)/g, "$1 "))).catch((e) => toast.error(errorMessage(e)));
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/5 py-2 text-sm">
      <span className="text-slate-400">Aadhaar</span>
      <span className="flex items-center gap-2">
        <span data-testid="kyc-review-aadhaar" className="font-mono text-slate-100">{full || `XXXX XXXX ${lead.kyc.aadhaar_last4}`}</span>
        {lead.aadhaar_full ? null : full ? (
          <button onClick={() => setFull(null)} data-testid="kyc-aadhaar-hide" className="text-xs text-slate-400 hover:text-white">Hide</button>
        ) : (
          <button onClick={show} data-testid="kyc-aadhaar-show" className="border border-orange-500/60 px-2 py-0.5 text-xs text-orange-300 hover:bg-orange-500/10">Show</button>
        )}
      </span>
    </div>
  );
};

export const KycReviewDialog = ({ kind, lead, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(lead?.kyc_note || ""); }, [lead]);
  if (!lead) return null;
  const k = lead.kyc || {};
  const name = lead.company_name || lead.full_name;

  const save = async (status) => {
    setBusy(true);
    try {
      await adminApi.patch(`/admin/leads/${kind}/${lead.id}/kyc`, { status, note });
      toast.success(`KYC ${status}`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kyc-review-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 font-heading text-xl">
            <PhotoThumb fileId={lead.kyc?.photo_file_id} testId="kyc-review-photo" size="h-16 w-14" />
            {name} <span className="font-mono text-sm font-normal text-orange-300">{lead.reference}</span> <KycBadge status={lead.kyc_status} />
          </DialogTitle>
        </DialogHeader>
        {!lead.kyc ? <p className="text-slate-400">This lead was submitted before KYC was required.</p> : (
          <>
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <AadhaarRow kind={kind} lead={lead} />
              <Row label="PAN" value={k.has_pan === false ? "No PAN card" : k.pan_number} testId="kyc-review-pan" />
              <Row label="Terms accepted" value={lead.terms_accepted_at ? new Date(lead.terms_accepted_at).toLocaleString("en-IN") : "Not recorded"} testId="kyc-review-terms" />
              <Row label="Payment method" value={k.payout_method === "upi" ? "UPI" : "Bank account"} testId="kyc-review-payout" />
              {k.payout_method === "upi" ? <Row label="UPI ID" value={k.upi_id} testId="kyc-review-upi" /> : (
                <>
                  <Row label="Account holder" value={k.account_holder_name} testId="kyc-review-holder" />
                  <Row label="Account number" value={k.account_number} testId="kyc-review-account" />
                  <Row label="IFSC" value={k.ifsc} testId="kyc-review-ifsc" />
                </>
              )}
              {lead.esic_number !== undefined && <Row label="ESIC" value={lead.esic_number} testId="kyc-review-esic" />}
            </div>
            <p data-testid="kyc-review-address" className="text-sm text-slate-300"><span className="text-slate-400">Address: </span>{lead.full_address || "Not provided"}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <DocPreview fileId={k.photo_file_id} label="Passport photo" testId="kyc-doc-photo" />
              <DocPreview fileId={k.aadhaar_file_id} label="Aadhaar" testId="kyc-doc-aadhaar" />
              {k.pan_file_id && <DocPreview fileId={k.pan_file_id} label="PAN" testId="kyc-doc-pan" />}
              {k.payout_method !== "upi" && <DocPreview fileId={k.passbook_file_id} label="Passbook" testId="kyc-doc-passbook" />}
            </div>
            <Textarea data-testid="kyc-review-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
              placeholder="Note (e.g. reason for rejection)" className="rounded-none border-white/10 bg-[#0B1120]" />
            {lead.kyc_reviewed_by && <p className="text-xs text-slate-500">Last reviewed by {lead.kyc_reviewed_by_name ? `${lead.kyc_reviewed_by_name} (${lead.kyc_reviewed_by})` : lead.kyc_reviewed_by} on {new Date(lead.kyc_reviewed_at).toLocaleString("en-IN")}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              {kind === "worker" && lead.kyc_status === "approved" && <IdCardButton lead={lead} />}
              <button disabled={busy} onClick={() => save("rejected")} data-testid="kyc-reject-button"
                className="border border-red-500/50 px-5 py-2.5 text-sm font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-50">Reject</button>
              <button disabled={busy} onClick={() => save("approved")} data-testid="kyc-approve-button"
                className="bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">Approve</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
